/**

  Validation: Generates a reactive `form` object that dynamically
              sets the following props for each field (or group thereof):

    • $dirty:   if the field was interacted with at least once
    • $invalid: if a field passes validation or not
    • $error:   if the field is _both_ $dirty and $invalid
    • $errors:  an array of the failed validations; contains the
                prop name, the failed rule, and an error message

  References:

    • @see https://github.com/vuetifyjs/vuetify/blob/master/packages/vuetify/src/mixins/validatable/index.ts
    • @see https://github.com/pikax/vue-composable/blob/master/packages/vue-composable/src/validation/validation.ts
    • @see https://github.com/logaretm/vee-validate/blob/next/packages/core/src/useField.ts

 */

import camelCase from 'lodash-es/camelCase';
import { computed, isRef, reactive, ref, unref, watch } from 'vue';

import { JsonError } from '@/models/jsonApi';

import { UseValidation, ValidationField, ValidationSchema } from './types';

/**
 * Helper function to access value of a reactive prop
 * @param val
 */
function unwrap(val) {
  return isRef(val) ? val.value : val;
}

/**
 * @deprecated Use the `useValidation` composable from Stash instead
 * @see https://stash.leaflink.com/vitepress/composables/useValidation.html
 */
export default function useValidation<S, V>(_schema: ValidationSchema, values?: V): UseValidation<S, V> {
  const form = {
    setValues,
    setErrors,
    setDirty,
    revalidate,
  };

  const schema = Object.entries(_schema).reduce((newSchema, [field, rules]) => {
    newSchema[field] = { ...rules };

    if (!newSchema[field].server) {
      newSchema[field].server = {
        $validator: () => true,
        $message: 'Server error', // setErrors replaces this with the server error message
      };
    }

    return newSchema;
  }, {});

  if (values) {
    setValues(values);
  }

  /**
   * Builds a reactive `form` that will respond to updates in user-data and
   * validate against them. This function can also be called recursively to build
   * out nested form "groups".
   * @param {ValidationSchema} schema An object describing the validation rules for each `value`.
   * @param {object?} values An data object of the fields to be validated.
   * @returns {ValidationGroup}
   */
  function buildGroup(schema: ValidationSchema, values) {
    const group: Record<string, ValidationField> = {};
    const validations: ValidationField[] = [];

    for (const property of Object.keys(schema)) {
      const rules = schema[property];
      const value = values[property];
      const field = buildField({ value, rules, property }) as ValidationField;

      validations.push(field); // just used internally. Easier to bind $dirty / $invalid things on an array
      group[property] = field; // returned to component for composition
    }

    const $errors = computed(() => {
      return validations
        .filter((v) => unwrap(v).$errors.length)
        .reduce((errors, v) => {
          return errors.concat(...unwrap(v).$errors);
        }, []);
    });

    const $dirty = computed(() => {
      return validations.some((x) => unref(x.$dirty));
    });

    const $invalid = computed(() => {
      return validations.some((x) => !!unwrap(x.$invalid));
    });

    const $allDirty = computed(() => {
      return validations.every((x) => x.$dirty);
    });

    return reactive({
      ...group,
      $errors,
      $invalid,
      $dirty,
      $allDirty,
    });
  }

  /**
   * Builds out a single field from the `schema` data. The result is a
   * reactive object comprised of a starting value plus the set of `rules`
   * used to validate any changes to it.
   * @param {object} field The configuration schema for this field
   * @param {any} field.value The default `value` of the field
   * @param {Validator} field.rules The list of rules used to validate `value`
   * @param {string} field.property The name of the field property.
   * @returns {ValidationField}
   */
  function buildField({ value, rules, property }): ValidationField {
    const results = {};
    const ruleKeys = Object.keys(rules);
    const $model = ref(value);
    const $dirty = ref(false);
    const $invalid = computed(() => !Object.values(results).every(unwrap));
    const $error = computed(() => $invalid.value && $dirty.value);

    const $errors = computed(() =>
      ruleKeys
        .filter((key) => !unwrap(results[key]))
        .map((key) =>
          reactive({
            $property: property,
            $validator: key,
            $message: typeof rules[key].$message === 'function' ? rules[key].$message(form) : rules[key].$message || '',
          }),
        ),
    );

    watch(
      $model,
      () => {
        $dirty.value = true;
      },
      { deep: true },
    );

    // calculate the validation results for this field's value
    ruleKeys.forEach((key) => {
      results[key] = computed(() => {
        return form && $dirty.value ? rules[key].$validator(unwrap($model), form) : true;
      });
    });

    return {
      $model,
      $error,
      $errors,
      $dirty,
      $invalid,
      ...results,
    };
  }

  /**
   * Parse a server-supplied Error reponse object, in JSON Error format, and
   * apply it to the relevant fields within the generated form.
   * @param {object} values A data object containing the values to be validated.
   * @returns {object} The object of form values, useful for composition.
   */
  function setValues(vals) {
    const validations = buildGroup(schema, vals);

    Object.defineProperties(form, Object.getOwnPropertyDescriptors(validations));
    values = vals;

    return vals;
  }

  /**
   * Parses a JSON error object and updates the relevant fields within
   * the form.
   * @param {JsonError[]} errors An array of JSON error objects.
   * @returns {ValidationGroup} A reference to the reactive form, useful for composition.
   */
  function setErrors(errors: JsonError[], parser: (pointer: string) => string) {
    errors.forEach((error) => {
      const pointer = error.source?.pointer || '';
      let field = (parser ? parser(pointer) : pointer?.split('/').pop()) || '';

      if (field) {
        field = camelCase(field);
      }

      const oldVal = unref(form[field]?.$model);
      const rules = schema[field];

      if (!rules) {
        return; // TODO: when field isn't found in schema, re-validate with form-level error
      }

      rules.server.$validator = (newVal) => newVal !== oldVal; // TODO: maybe hide error indefinitely after user interaction
      rules.server.$message = error.detail;
    });

    revalidate();

    return form;
  }

  /**
   * Set $dirty propety for each field to true
   * @param {boolean} flag to set the $dirty property, default to true (make all fields $dirty)
   */
  function setDirty(flag = true) {
    Object.keys(values || {}).forEach((key) => {
      form[key].$dirty.value = flag;
    });
  }

  /**
   * Force revalidation for each field
   */
  function revalidate() {
    setDirty(false);
    setDirty(true);
  }

  return {
    form,
    revalidate,
    setValues,
    setErrors,
    setDirty,
    /* eslint-disable @typescript-eslint/no-explicit-any */
  } as any;
}
